import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@cieda/djayma-ui";
import {
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import type { AdminNotification } from "@/lib/admin-api/notifications";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NotificationListProps {
  readonly notifications: readonly AdminNotification[];
  readonly onMarkRead?: (id: string) => void;
  /** When true, renders a compact variant for the dropdown */
  readonly compact?: boolean;
}

// ---------------------------------------------------------------------------
// Severity icon mapping
// ---------------------------------------------------------------------------

const SEVERITY_ICON_MAP = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle2,
} as const;

const SEVERITY_COLOR_MAP = {
  info: "text-blue-500 bg-blue-50",
  warning: "text-amber-500 bg-amber-50",
  error: "text-red-500 bg-red-50",
  success: "text-emerald-500 bg-emerald-50",
} as const;

// ---------------------------------------------------------------------------
// Time-ago helper (French)
// ---------------------------------------------------------------------------

function timeAgo(dateString: string): string {
  const now = Date.now();
  const date = new Date(dateString).getTime();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / 60_000);

  if (diffMinutes < 1) return "À l'instant";
  if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours > 1 ? "s" : ""}`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `Il y a ${diffDays} jour${diffDays > 1 ? "s" : ""}`;
  }

  return new Intl.DateTimeFormat("fr-SN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(dateString));
}

// ---------------------------------------------------------------------------
// Single notification item
// ---------------------------------------------------------------------------

function NotificationRow({
  notification,
  compact,
  onMarkRead,
  onNavigate,
}: {
  notification: AdminNotification;
  compact: boolean;
  onMarkRead?: (id: string) => void;
  onNavigate?: (link: string) => void;
}) {
  const Icon = SEVERITY_ICON_MAP[notification.severity] ?? Info;
  const colorClasses = SEVERITY_COLOR_MAP[notification.severity] ?? "text-stone-500 bg-stone-50";

  const handleClick = useCallback(() => {
    if (!notification.is_read) {
      onMarkRead?.(notification.id);
    }
    if (notification.link) {
      onNavigate?.(notification.link);
    }
  }, [notification, onMarkRead, onNavigate]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "flex w-full items-start gap-3 text-left transition-colors",
        compact ? "px-3 py-2.5" : "px-4 py-3",
        !notification.is_read
          ? "bg-amber-50/40 hover:bg-amber-50/60"
          : "hover:bg-stone-50",
        notification.link && "cursor-pointer",
      )}
    >
      {/* Severity icon */}
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full",
          compact ? "h-8 w-8" : "h-9 w-9",
          colorClasses,
        )}
      >
        <Icon className={compact ? "h-4 w-4" : "h-4.5 w-4.5"} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "text-sm font-medium text-stone-900 truncate",
              !notification.is_read && "font-semibold",
            )}
          >
            {notification.title}
          </p>
          {/* Unread dot */}
          {!notification.is_read && (
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
          )}
        </div>
        <p
          className={cn(
            "text-stone-500 truncate",
            compact ? "text-xs mt-0.5" : "text-sm mt-0.5",
          )}
        >
          {notification.message}
        </p>
        <p className="mt-1 text-xs text-stone-400">
          {timeAgo(notification.created_at)}
        </p>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationList({
  notifications,
  onMarkRead,
  compact = false,
}: NotificationListProps) {
  const navigate = useNavigate();

  const handleNavigate = useCallback(
    (link: string) => {
      navigate(link);
    },
    [navigate],
  );

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
        <p className="text-sm text-stone-400">Aucune notification</p>
      </div>
    );
  }

  return (
    <div className={cn("divide-y divide-stone-100", compact && "max-h-80 overflow-y-auto")}>
      {notifications.map((notification) => (
        <NotificationRow
          key={notification.id}
          notification={notification}
          compact={compact}
          onMarkRead={onMarkRead}
          onNavigate={handleNavigate}
        />
      ))}
    </div>
  );
}
